
import { translations } from '@/translations';
import type { Language } from './types';

export class TranslationSuggestionGenerator {
  public generateSmartTranslationSuggestions(keyPath: string, englishValue: string, targetLang: Language): string[] {
    const suggestions: string[] = [];
    const category = keyPath.split('.')[0];

    // Context-aware translations based on category and common patterns
    const contextualSuggestions = this.getContextualSuggestions(category, englishValue, targetLang);
    suggestions.push(...contextualSuggestions);

    // Pattern-based suggestions from existing translations
    const patternSuggestions = this.getPatternBasedSuggestions(englishValue, targetLang);
    suggestions.push(...patternSuggestions);

    // Fallback to enhanced prefixed version
    const prefix = targetLang === 'pt' ? '[PT]' : '[ES]';
    suggestions.push(`${prefix} ${englishValue}`);

    return [...new Set(suggestions)]; // Remove duplicates
  }

  private getContextualSuggestions(category: string, englishValue: string, targetLang: Language): string[] {
    const suggestions: string[] = [];
    
    // Common UI patterns
    const commonTranslations = {
      pt: {
        'Save': 'Salvar',
        'Cancel': 'Cancelar',
        'Submit': 'Enviar',
        'Back': 'Voltar',
        'Next': 'Próximo',
        'Previous': 'Anterior',
        'Continue': 'Continuar',
        'Login': 'Entrar',
        'Logout': 'Sair',
        'Register': 'Registrar',
        'Email': 'E-mail',
        'Password': 'Senha',
        'Name': 'Nome',
        'Loading': 'Carregando',
        'Error': 'Erro',
        'Success': 'Sucesso',
        'Warning': 'Aviso',
        'Delete': 'Excluir',
        'Edit': 'Editar',
        'View': 'Visualizar',
        'Close': 'Fechar',
        'Open': 'Abrir',
        'Settings': 'Configurações',
        'Profile': 'Perfil',
        'Dashboard': 'Painel',
        'Home': 'Início'
      },
      es: {
        'Save': 'Guardar',
        'Cancel': 'Cancelar',
        'Submit': 'Enviar',
        'Back': 'Volver',
        'Next': 'Siguiente',
        'Previous': 'Anterior',
        'Continue': 'Continuar',
        'Login': 'Iniciar sesión',
        'Logout': 'Cerrar sesión',
        'Register': 'Registrarse',
        'Email': 'Correo electrónico',
        'Password': 'Contraseña',
        'Name': 'Nombre',
        'Loading': 'Cargando',
        'Error': 'Error',
        'Success': 'Éxito',
        'Warning': 'Advertencia',
        'Delete': 'Eliminar',
        'Edit': 'Editar',
        'View': 'Ver',
        'Close': 'Cerrar',
        'Open': 'Abrir',
        'Settings': 'Configuración',
        'Profile': 'Perfil',
        'Dashboard': 'Panel',
        'Home': 'Inicio'
      }
    };

    const langTranslations = commonTranslations[targetLang];
    if (langTranslations && langTranslations[englishValue as keyof typeof langTranslations]) {
      suggestions.push(langTranslations[englishValue as keyof typeof langTranslations]);
    }

    return suggestions;
  }

  private getPatternBasedSuggestions(englishValue: string, targetLang: Language): string[] {
    const suggestions: string[] = [];
    
    try {
      const langTranslations = translations[targetLang];
      
      // Look for similar patterns in existing translations
      const allExistingTranslations = this.getAllTranslationPairs(langTranslations);
      
      // Find translations with similar English patterns
      const similarPatterns = allExistingTranslations.filter(pair => {
        const englishWords = englishValue.toLowerCase().split(' ');
        const pairWords = pair.value.toLowerCase().split(' ');
        return englishWords.some(word => pairWords.includes(word));
      });

      if (similarPatterns.length > 0) {
        // Use the most similar pattern as a base
        suggestions.push(similarPatterns[0].value);
      }
    } catch (error) {
      console.warn('Could not generate pattern-based suggestions:', error);
    }

    return suggestions;
  }

  private getAllTranslationPairs(obj: unknown, prefix: string = ''): Array<{key: string, value: string}> {
    const pairs: Array<{key: string, value: string}> = [];
    
    if (typeof obj !== 'object' || obj === null) {
      return pairs;
    }

    const typedObj = obj as Record<string, unknown>;
    
    for (const key in typedObj) {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      const value = typedObj[key];
      
      if (typeof value === 'object' && value !== null) {
        pairs.push(...this.getAllTranslationPairs(value, fullKey));
      } else if (typeof value === 'string') {
        pairs.push({ key: fullKey, value });
      }
    }
    
    return pairs;
  }
}
